import { useId, type ReactElement } from 'react';
import { Chip } from '@/components/atoms/chip';
import { FormInput } from '@/components/molecules/form-input';
import { Micro } from '@/components/atoms/micro';
import {
  CONTRIBUTE_COMPOSER_MODES,
  CONTRIBUTE_REGION_OPTIONS,
  SUMMARY_MAX_LENGTH,
  type ContributeComposerMode,
  type ContributeRegion,
} from '@/lib/contribute-form';

const COMPOSER_MODE_LABELS: Record<ContributeComposerMode, string> = {
  traditional: 'Traditional',
  named: 'Named',
  unknown: 'Unknown',
};

export type ContributeComposerSectionProps = {
  composerMode: ContributeComposerMode | null;
  composerName: string;
  composerYear: string;
  region: ContributeRegion | null;
  summary: string;
  onModeChange: (mode: ContributeComposerMode) => void;
  onNameChange: (name: string) => void;
  onYearChange: (year: string) => void;
  onRegionChange: (region: ContributeRegion) => void;
  onSummaryChange: (summary: string) => void;
};

export function ContributeComposerSection({
  composerMode,
  composerName,
  composerYear,
  region,
  summary,
  onModeChange,
  onNameChange,
  onYearChange,
  onRegionChange,
  onSummaryChange,
}: ContributeComposerSectionProps): ReactElement {
  const modeGroupName = useId();
  const summaryId = useId();

  return (
    <div className="flex flex-col gap-5">
      <div role="radiogroup" aria-label="Composer" className="flex flex-wrap gap-3">
        {CONTRIBUTE_COMPOSER_MODES.map((mode) => (
          <label
            key={mode}
            className="inline-flex items-center gap-2 text-sm text-[var(--ink)] cursor-pointer"
          >
            <input
              type="radio"
              name={modeGroupName}
              value={mode}
              checked={composerMode === mode}
              onChange={() => onModeChange(mode)}
              className="h-4 w-4"
            />
            {COMPOSER_MODE_LABELS[mode]}
          </label>
        ))}
      </div>

      {composerMode === 'named' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <FormInput
            label="Composer name"
            value={composerName}
            onChange={(event) => onNameChange(event.target.value)}
          />
          <FormInput
            label="Composer year"
            value={composerYear}
            inputMode="numeric"
            onChange={(event) => onYearChange(event.target.value)}
          />
        </div>
      )}

      <div role="group" aria-label="Region" className="flex flex-wrap gap-1.5">
        {CONTRIBUTE_REGION_OPTIONS.map((option) => {
          const isActive = region === option.value;
          return (
            <Chip
              key={option.value}
              active={isActive}
              aria-pressed={isActive}
              onClick={() => onRegionChange(option.value)}
            >
              {option.label}
            </Chip>
          );
        })}
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <Micro as="label" htmlFor={summaryId}>
            Summary
          </Micro>
          <span className="text-[10px] text-[var(--ink-3)]">
            {summary.length}/{SUMMARY_MAX_LENGTH}
          </span>
        </div>
        <textarea
          id={summaryId}
          value={summary}
          onChange={(event) => onSummaryChange(event.target.value)}
          maxLength={SUMMARY_MAX_LENGTH}
          rows={3}
          className="rounded border border-[var(--rule)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--ink)] focus:outline-none focus:border-[var(--ink)]"
        />
      </div>
    </div>
  );
}
